/**
 * firstPartyRedirect.js — client-side first-party redirect allowlist (999.2085).
 *
 * Mirrors the backend SSOT auth-backend/src/lib/first-party-redirect.js (there
 * is no shared package between the two, so the host rules are duplicated by
 * necessity — keep them in sync). Used to stop open redirects on navigation
 * targets that come from a URL param (TOTP verify success, logout) the way the
 * backend already stops them on the /api/auth/authorize redirect_uri.
 *
 * A redirect target is first-party when, after WHATWG-URL parsing (relative
 * targets resolve against the current origin):
 *   - it is same-origin as the app, or
 *   - its exact origin is in ALLOWED_ORIGINS (the SAME check the backend runs
 *     BEFORE its isNonProd() gate, first-party-redirect.js:79) — this is what
 *     keeps prod cross-origin first-party apps (climbrs.raikegroup.com,
 *     strivers.raikegroup.com, billing.raikegroup.com, ...) trusted in
 *     PRODUCTION, or
 *   - NON-PRODUCTION ONLY (999.16406, mirroring the backend's 999.16230 fix):
 *     its host is loopback (any http(s) scheme/port), or its https host
 *     matches a first-party pattern (localdev / CI-UAT raike.local
 *     multi-label / prod single-label raikegroup.com).
 * In production the only trusted paths are same-origin and the exact-origin
 * ALLOWED_ORIGINS match above — the loopback and host-pattern checks are
 * unreachable once !isNonProd(), same as
 * auth-backend/src/lib/first-party-redirect.js's isNonProd() gate. Before the
 * 999.16406 fix, this file had NO NODE_ENV gate at all: production still
 * trusted loopback/*.raike.local/*.localdev.raikegroup.com as first-party,
 * the exact open-redirect class 999.16230 closed on the backend (harrison+law,
 * 2026-08-31) — LogoutPage.jsx and TOTPVerifyPage.jsx both do an unchecked
 * `window.location.href = redirectUrl` after this returns true. An earlier
 * pass of that fix dropped the exact-origin allowlist entirely (harrison
 * BLOCK, 999.16406 re-review): that broke the prod TOTP-verify →
 * redirect-back-to-product flow for every cross-origin app, since same-origin
 * alone never matches auth.raikegroup.com redirecting to climbrs.raikegroup.com.
 * Non-http(s) schemes (javascript:, data:) are rejected. Validation is on the
 * PARSED hostname, so @userinfo / path / backslash tricks cannot smuggle a
 * foreign authority past a host pattern.
 */

// Exact-origin allowlist — mirrors deploy/auth-backend.yaml's ALLOWED_ORIGINS
// env var (the backend reads it via config.getList('ALLOWED_ORIGINS'); the
// frontend has no runtime env plumbing for it — CRA bakes env at build time
// and no REACT_APP_ var carries this today), checked BEFORE the isNonProd()
// gate below, same order as the backend's isFirstPartyRedirect. Hand-kept in
// sync with deploy/auth-backend.yaml, same maintenance model as
// FIRST_PARTY_HOST_PATTERNS below (999.16406 harrison BLOCK: dropping this
// broke every cross-origin first-party redirect in production).
const ALLOWED_ORIGINS = [
  'https://resume-optimizer-frontend-vl525q4h7q-uc.a.run.app',
  'https://auth-frontend-vl525q4h7q-uc.a.run.app',
  'https://juggler-frontend-vl525q4h7q-uc.a.run.app',
  'https://payment-frontend-vl525q4h7q-uc.a.run.app',
  'https://strivers.raikegroup.com',
  'https://climbrs.raikegroup.com',
  'https://billing.raikegroup.com',
  'https://auth.raikegroup.com',
  'https://bugs.raikegroup.com',
  'https://www.raikegroup.com',
  'https://scout.raikegroup.com',
  'https://scout-frontend-1057418149210.us-central1.run.app',
];

// Same allowlist as the backend's isNonProd (auth-backend/src/lib/
// first-party-redirect.js, auth-backend/src/app.js:57) — an ALLOWLIST, not a
// denylist on the literal string 'production', so an unset/misspelled/
// differently-cased NODE_ENV fails closed rather than fails open.
const isNonProd = () => ['development', 'test', 'local'].includes(process.env.NODE_ENV);

const FIRST_PARTY_HOST_PATTERNS = [
  /^[a-z0-9-]+\.localdev\.raikegroup\.com$/i,
  /^([a-z0-9-]+\.)+raike\.local$/i,
  /^[a-z0-9-]+\.raikegroup\.com$/i,
];

function isLoopbackHost(host) {
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';
}

export function isFirstPartyRedirect(uri) {
  if (!uri || typeof uri !== 'string') return false;

  let u;
  try {
    // Base = current origin so relative targets ("/account") resolve same-origin;
    // "//evil.com" resolves to a foreign origin and is caught below.
    u = new URL(uri, window.location.origin);
  } catch (_) {
    return false;
  }

  if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
  if (u.origin === window.location.origin) return true;
  if (ALLOWED_ORIGINS.indexOf(u.origin) !== -1) return true;

  if (!isNonProd()) {
    return false; // fail-closed: only a known non-prod env treats loopback/localdev/pattern hosts as first-party
  }

  const host = u.hostname;
  if (isLoopbackHost(host)) return true;
  if (u.protocol !== 'https:') return false;

  return FIRST_PARTY_HOST_PATTERNS.some((p) => p.test(host));
}

export default isFirstPartyRedirect;
